import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  CloseCircleFilled,
  CloseOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Alert, Button, Input, Popover, Select } from 'antd';
import ContentLayout from '../components/ContentLayout';
import TableSection from '../components/TableSection';
import DeleteModal from '../../../components/modals/DeleteModal';
import { useAuth } from '../../../context/useAuth';
import { PAGE_SIZE } from '../../../config';
import CreateUserModal from '../components/modals/CreateUserModal';
import UpdateUserModal from '../components/modals/UpdateUserModal';
import AddTherapyModal from '../components/modals/AddTherapyModal';
import SendNotificationModal from '../components/modals/SendNotificationModal';
import ImportUsersModal from '../components/modals/ImportUsersModal';
import PermanentDeleteUserModal from '../components/modals/PermanentDeleteUserModal';
import { useChatNotifications } from '../../chat/context/useChatNotifications';
import ViewUserDetailsModal from '../../therapies/components/modals/ViewUserDetailsModal';
import TherapyOccurrencesModal from '../../therapies/components/modals/TherapyOccurrencesModal';
import {
  fetchUserTherapyModalContext,
  fetchUserTherapyReservation,
  getTherapyModalContextFromResponse,
  getTherapyReservationFromResponse,
  mergeTherapyContextReservation,
} from '../../therapies/api/therapiesApi';
import {
  bulkUpdateUserStatus,
  fetchUserById,
  fetchUsers,
  updateUser,
  updateUserPassword,
} from '../api/usersApi';
import useUsersHandlers from '../hooks/useUsersHandlers';
import { userPickupMethodOptions } from '../constants/userConstants';
import style from '../components/users.module.scss';
import useTableSearch from '../../../shared/hooks/useTableSearch';
import StatusFlag from '../../../components/elements/StatusFlag';
import {
  getAccountStatusTone,
  getBooleanLabel,
  getBooleanTone,
} from '../../../shared/utils/statusTones';
import { isAdmin } from '../../../shared/utils/roles';

const formatAddress = (address) => {
  if (!address || typeof address !== 'object') return '-';

  const parts = [address.street, address.city].filter(Boolean);

  return parts.length ? parts.join(', ') : '-';
};

const buildTherapyModalUserFallback = (rowUser) =>
  rowUser
    ? {
        userId: rowUser._id,
        userName:
          `${rowUser.firstName || ''} ${rowUser.lastName || ''}`.trim() ||
          rowUser.email ||
          rowUser.phone,
        firstName: rowUser.firstName,
        lastName: rowUser.lastName,
        userEmail: rowUser.email,
        email: rowUser.email,
        userPhone: rowUser.phone,
        phone: rowUser.phone,
        userInternalNote: rowUser.internalNote,
        internalNote: rowUser.internalNote,
      }
    : null;

export default function UsersPage() {
  const { user } = useAuth();
  const { refreshUnread } = useChatNotifications();

  // Jedinstvena pretraga (Pacijent/LBO/BZK/Telefon) - src/config/searchConfig.js.
  const tableSearch = useTableSearch('users');
  const { reset: resetTableSearch } = tableSearch;
  const {
    userSearch: debouncedUserSearch,
    lboSearch: debouncedLboSearch,
    bzkSearch: debouncedBzkSearch,
    phoneSearch: debouncedPhoneSearch,
  } = tableSearch.params;
  const [userBirthdaySearch, setUserBirthdaySearch] = useState('');
  const [birthdayPickerOpen, setBirthdayPickerOpen] = useState(false);
  const [birthdayDraft, setBirthdayDraft] = useState({
    day: '',
    month: '',
    year: '',
  });

  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isImportUsersModalOpen, setIsImportUsersModalOpen] = useState(false);
  const [isUpdateUserModalOpen, setIsUpdateUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isDeletePasswordVisible, setIsDeletePasswordVisible] = useState(false);

  const [users, setUsers] = useState([]);
  const [userForUpdate, setUserForUpdate] = useState(null);
  const [userForView, setUserForView] = useState(null);
  const [userForVerification, setUserForVerification] = useState(null);
  const [userForBlock, setUserForBlock] = useState(null);
  const [userForDelete, setUserForDelete] = useState(null);
  const [deleteUserId, setDeleteUser] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  // Trajno brisanje (DELETE /users/:id/permanent) - odvojeno od gornje trajne
  // deaktivacije (soft delete), koja koristi deleteUserId/deletePassword.
  const [userForErase, setUserForErase] = useState(null);
  const [isEraseModalOpen, setIsEraseModalOpen] = useState(false);
  const [therapyForOccurrences, setTherapyForOccurrences] = useState(null);
  const [isTherapiesModalOpen, setIsTherapiesModalOpen] = useState(false);
  const [therapiesLoadingUserId, setTherapiesLoadingUserId] = useState(null);
  const [userForTherapyCreate, setUserForTherapyCreate] = useState(null);
  const [isTherapyCreateOpen, setIsTherapyCreateOpen] = useState(false);
  const [userForNotification, setUserForNotification] = useState(null);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isViewUserDetailsModalOpen, setIsViewUserDetailsModalOpen] =
    useState(false);

  const [updateLoading, setUpdateLoading] = useState(false);
  const [viewUserDetailsLoading, setViewUserDetailsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const [message, setMessage] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('active');
  const [bulkLoading, setBulkLoading] = useState(false);
  const latestUsersRequestRef = useRef(0);

  const handleOpenNotification = (rowUser) => {
    setUserForNotification(rowUser);
    setIsNotificationModalOpen(true);
  };

  const handleCloseNotification = () => {
    setIsNotificationModalOpen(false);
  };

  const handleViewUserDetailsModalClose = () => {
    setIsViewUserDetailsModalOpen(false);
    setUserForView(null);
    setViewUserDetailsLoading(false);
  };

  const handleViewUserDetails = useCallback(async (userId) => {
    setViewUserDetailsLoading(true);
    try {
      const { data, ok, message } = await fetchUserById(userId);
      const userData =
        data?.user || data?.data?.user || data?.data || data || null;

      if (!ok || !userData) {
        setMessage(message || 'Greska pri ucitavanju korisnika');
        return;
      }

      setUserForView(userData);
      setIsViewUserDetailsModalOpen(true);
    } finally {
      setViewUserDetailsLoading(false);
    }
  }, []);

  const handleOpenTherapies = useCallback(async (rowUser) => {
    if (!rowUser?._id) return;

    const fallbackTherapyContext = buildTherapyModalUserFallback(rowUser);
    setTherapiesLoadingUserId(rowUser._id);

    try {
      const { data, ok, status, message } = await fetchUserTherapyModalContext(
        rowUser._id
      );

      if (!ok) {
        if (status === 404) {
          setTherapyForOccurrences(fallbackTherapyContext);
          setIsTherapiesModalOpen(true);
          return;
        }

        setMessage(message || 'Doslo je do greske pri dobavljanju terapija.');
        return;
      }

      const therapyModalContext = getTherapyModalContextFromResponse(data);

      const resolvedTherapyContext = therapyModalContext || fallbackTherapyContext;
      const { data: reservationData, ok: reservationOk } =
        await fetchUserTherapyReservation(rowUser._id);
      const enrichedTherapyContext = mergeTherapyContextReservation(
        resolvedTherapyContext,
        reservationOk
          ? getTherapyReservationFromResponse(reservationData)
          : null
      );

      setTherapyForOccurrences(
        enrichedTherapyContext || fallbackTherapyContext
      );
      setIsTherapiesModalOpen(true);
    } finally {
      setTherapiesLoadingUserId(null);
    }
  }, []);

  const handleCloseTherapies = () => {
    setIsTherapiesModalOpen(false);
    setTherapyForOccurrences(null);
  };

  const handleReloadTherapyContext = useCallback(async (therapyContext) => {
    if (!therapyContext?.userId) return null;

    const { data, ok } = await fetchUserTherapyModalContext(
      therapyContext.userId
    );

    if (!ok) return null;

    const refreshedTherapyContext = getTherapyModalContextFromResponse(data);
    if (!refreshedTherapyContext) return null;

    const { data: reservationData, ok: reservationOk } =
      await fetchUserTherapyReservation(therapyContext.userId);
    const enrichedTherapyContext = mergeTherapyContextReservation(
      refreshedTherapyContext,
      reservationOk
        ? getTherapyReservationFromResponse(reservationData)
        : null
    );

    setTherapyForOccurrences(enrichedTherapyContext);
    return enrichedTherapyContext;
  }, []);

  const handleOpenAddTherapy = (rowUser) => {
    setUserForTherapyCreate(rowUser);
    setIsTherapyCreateOpen(true);
  };

  const handleCloseAddTherapy = () => {
    setIsTherapyCreateOpen(false);
  };

  const handleFilterChange = (newFilter) => {
    setCurrentPage(1);
    setFilter((prev) => ({ ...prev, ...newFilter }));
  };

  const searchResetSkipRef = useRef(true);
  useEffect(() => {
    if (searchResetSkipRef.current) {
      searchResetSkipRef.current = false;
      return;
    }
    // Kada se pretraga/filter promeni, vrati se na prvu stranicu da pretraga
    // gleda ceo (filtrirani) skup, a ne da ostane na staroj stranici.
    setCurrentPage(1);
  }, [
    debouncedUserSearch,
    debouncedLboSearch,
    debouncedBzkSearch,
    debouncedPhoneSearch,
    userBirthdaySearch,
    filter,
  ]);

  const loadUsers = useCallback(async (page = currentPage) => {
    const requestId = latestUsersRequestRef.current + 1;
    latestUsersRequestRef.current = requestId;
    setListLoading(true);

    const { data, ok, message } = await fetchUsers({
      page,
      limit: PAGE_SIZE,
      filter: { ...filter, role: 'user' },
      search: debouncedUserSearch,
      userSearch: debouncedUserSearch,
      lboSearch: debouncedLboSearch,
      bzkSearch: debouncedBzkSearch,
      phoneSearch: debouncedPhoneSearch,
      userBirthdaySearch: userBirthdaySearch || null,
    });

    if (requestId !== latestUsersRequestRef.current) {
      return;
    }

    setListLoading(false);

    if (ok && data) {
      const resolvedPage = data?.pagination?.currentPage || 1;
      setUsers(data.users || []);
      if (resolvedPage !== page) {
        setCurrentPage(resolvedPage);
      }
      setTotalPages(data?.pagination?.totalPages || 1);
      setTotalResults(data?.pagination?.totalCount ?? null);
    } else if (message) {
      setMessage(message);
    }
  }, [
    currentPage,
    debouncedBzkSearch,
    debouncedLboSearch,
    debouncedPhoneSearch,
    debouncedUserSearch,
    filter,
    userBirthdaySearch,
  ]);

  const handleRefreshUserForUpdate = useCallback(async () => {
    if (!userForUpdate?._id) return;

    const { data, ok } = await fetchUserById(userForUpdate._id);
    const userData =
      data?.user || data?.data?.user || data?.data || data || null;

    if (ok && userData) {
      setUserForUpdate(userData);
    }
  }, [userForUpdate?._id]);

  const handleRefreshUserForView = useCallback(async () => {
    if (!userForView?._id) return;

    const { data, ok } = await fetchUserById(userForView._id);
    const userData =
      data?.user || data?.data?.user || data?.data || data || null;

    if (ok && userData) {
      setUserForView(userData);
    }
  }, [userForView?._id]);

  const handleViewUserDetailsSubmit = useCallback(
    async (formData) => {
      if (!userForView?._id) {
        return { ok: false, message: 'Korisnik nije izabran.' };
      }

      setViewUserDetailsLoading(true);
      try {
        const { data, ok, message, status, code } = await updateUser(
          userForView._id,
          formData,
          userForView
        );

        if (!ok) {
          setMessage(message || 'Doslo je do greske pri azuriranju korisnika.');
          return { ok: false, message, status, code };
        }

        const updatedUser =
          data?.user || data?.data?.user || data?.data || data || null;

        if (updatedUser) {
          setUserForView(updatedUser);
        }

        loadUsers();
        return { ok: true, message: message || 'Korisnik uspesno azuriran.' };
      } finally {
        setViewUserDetailsLoading(false);
      }
    },
    [loadUsers, userForView]
  );

  const handleViewUserPasswordSubmit = useCallback(
    async (password) => {
      if (!userForView?._id) {
        return { ok: false, message: 'Korisnik nije izabran.' };
      }

      setViewUserDetailsLoading(true);
      try {
        const { data, ok, message } = await updateUserPassword(
          userForView._id,
          password
        );

        if (!ok) {
          setMessage(
            message || 'Doslo je do greske pri azuriranju lozinke korisnika.'
          );
          return { ok: false, message };
        }

        const updatedUser =
          data?.user || data?.data?.user || data?.data || data || null;

        if (updatedUser) {
          setUserForView(updatedUser);
        }

        loadUsers();
        return {
          ok: true,
          message: message || 'Lozinka korisnika uspesno azurirana.',
        };
      } finally {
        setViewUserDetailsLoading(false);
      }
    },
    [loadUsers, userForView]
  );

  useEffect(() => {
    loadUsers(currentPage);
  }, [loadUsers]);

  useEffect(() => {
    setSelectedUserIds([]);
  }, [
    currentPage,
    debouncedBzkSearch,
    debouncedLboSearch,
    debouncedPhoneSearch,
    debouncedUserSearch,
    filter,
    userBirthdaySearch,
  ]);

  const handleBulkStatusUpdate = useCallback(async () => {
    if (!selectedUserIds.length) return;

    setBulkLoading(true);
    try {
      const { ok, message, data } = await bulkUpdateUserStatus({
        ids: selectedUserIds,
        status: bulkStatus,
      });

      if (!ok) {
        setMessage(
          message || 'Doslo je do greske pri masovnoj izmeni statusa.'
        );
        return;
      }

      const modifiedCount = data?.modifiedCount ?? 0;
      setMessage(
        `${message || 'Statusi korisnika azurirani.'} (${modifiedCount})`
      );
      setSelectedUserIds([]);
      loadUsers();
    } finally {
      setBulkLoading(false);
    }
  }, [bulkStatus, loadUsers, selectedUserIds]);

  const {
    handleActiveUser,
    handleBlockUser,
    handleCreateUserModalClose,
    handleDeleteModalClose,
    handleDeleteUser,
    handleUpdateModalClose,
    handleUpdateUser,
    handleUpdateUserPasswordSubmit,
    handleUpdateUserSubmit,
  } = useUsersHandlers({
    deleteUserId,
    deletePassword,
    loadUsers,
    setDeleteError,
    setDeletePassword,
    setDeleteUser,
    setDone,
    setIsCreateUserModalOpen,
    setIsDeleteLoading,
    setIsDeleteModalOpen,
    setIsUpdateUserModalOpen,
    setLoading,
    setMessage,
    setUpdateLoading,
    setUserForUpdate,
    setUserForVerification,
    setUsers,
    userForUpdate,
  });

  const statusLabel = (status) => {
    const map = {
      active: 'Aktivan',
      blocked: 'Blokiran',
      deleted: 'Trajno deaktiviran',
    };

    return map[status] || status || '-';
  };

  const getUserDisplayName = useCallback((targetUser) => {
    if (!targetUser) return 'ovog korisnika';

    const fullName =
      `${targetUser.firstName || ''} ${targetUser.lastName || ''}`.trim();

    return (
      fullName ||
      targetUser.email ||
      targetUser.phone ||
      targetUser._id ||
      'ovog korisnika'
    );
  }, []);

  const handleOpenBlockModal = useCallback((rowUser) => {
    setUserForBlock(rowUser);
    setDone(false);
    setDeleteError('');
    setIsBlockModalOpen(true);
  }, []);

  const handleCloseBlockModal = useCallback(() => {
    setIsBlockModalOpen(false);
    setUserForBlock(null);
    setDone(false);
    setDeleteError('');
  }, []);

  const handleConfirmBlockUser = useCallback(async () => {
    if (!userForBlock?._id) return;

    const result = await handleBlockUser(userForBlock._id);

    if (result?.ok) {
      handleCloseBlockModal();
    }
  }, [handleBlockUser, handleCloseBlockModal, userForBlock]);

  const handleClosePermanentDeleteModal = useCallback(() => {
    setUserForDelete(null);
    setIsDeletePasswordVisible(false);
    handleDeleteModalClose();
  }, [handleDeleteModalClose]);

  const handleOpenEraseModal = (rowUser) => {
    setUserForErase(rowUser);
    setIsEraseModalOpen(true);
  };

  const handleCloseEraseModal = () => {
    setIsEraseModalOpen(false);
    setUserForErase(null);
  };

  // Pacijent je nepovratno obrisan: ukloni ga iz liste i zatvori sve sto ga jos
  // prikazuje (detalj, izmena, terapije...), pa osvezi listu i broj neprocitanih
  // poruka - bek ne salje socket event za obrisan razgovor. Ostale liste
  // (Terapije, Rezervisane/Sacuvane Th, Zahtevi, Poruke, logovi) nemaju kes i
  // ponovo se ucitavaju pri otvaranju stranice.
  const handleUserErased = (erasedUserId) => {
    setUsers((prev) => prev.filter((row) => row._id !== erasedUserId));
    setSelectedUserIds((prev) => prev.filter((id) => id !== erasedUserId));

    if (userForView?._id === erasedUserId) handleViewUserDetailsModalClose();
    if (userForUpdate?._id === erasedUserId) handleUpdateModalClose();
    if (therapyForOccurrences?.userId === erasedUserId) handleCloseTherapies();
    if (userForTherapyCreate?._id === erasedUserId) handleCloseAddTherapy();
    if (userForNotification?._id === erasedUserId) handleCloseNotification();
    if (userForBlock?._id === erasedUserId) handleCloseBlockModal();
    if (deleteUserId === erasedUserId) handleClosePermanentDeleteModal();

    loadUsers();
    refreshUnread();
  };

  const formatBirthdayParts = useCallback((dateValue) => {
    if (!dateValue) {
      return { day: '', month: '', year: '' };
    }

    const parsedDate = dayjs(dateValue);

    if (!parsedDate.isValid()) {
      return { day: '', month: '', year: '' };
    }

    return {
      day: parsedDate.format('DD'),
      month: parsedDate.format('MM'),
      year: parsedDate.format('YYYY'),
    };
  }, []);

  const updateBirthdayDraftField = useCallback((field, nextValue) => {
    const maxLengths = {
      day: 2,
      month: 2,
      year: 4,
    };

    setBirthdayDraft((prev) => ({
      ...prev,
      [field]: nextValue.replace(/\D/g, '').slice(0, maxLengths[field]),
    }));
  }, []);

  const buildBirthdayIso = useCallback((draftValue) => {
    const normalizedDay = draftValue.day ? draftValue.day.padStart(2, '0') : '';
    const normalizedMonth = draftValue.month
      ? draftValue.month.padStart(2, '0')
      : '';
    const { year } = draftValue;

    if (!normalizedDay && !normalizedMonth && !year) {
      return '';
    }

    if (
      !normalizedDay ||
      !normalizedMonth ||
      year.length !== 4 ||
      normalizedDay.length !== 2 ||
      normalizedMonth.length !== 2
    ) {
      return null;
    }

    const dayNumber = Number(normalizedDay);
    const monthNumber = Number(normalizedMonth);
    const yearNumber = Number(year);

    if (
      dayNumber < 1 ||
      dayNumber > 31 ||
      monthNumber < 1 ||
      monthNumber > 12 ||
      yearNumber < 1900 ||
      yearNumber > 2100
    ) {
      return null;
    }

    const parsedDate = new Date(yearNumber, monthNumber - 1, dayNumber);

    if (
      parsedDate.getFullYear() !== yearNumber ||
      parsedDate.getMonth() !== monthNumber - 1 ||
      parsedDate.getDate() !== dayNumber
    ) {
      return null;
    }

    return `${year}-${normalizedMonth}-${normalizedDay}`;
  }, []);

  const birthdayDisplayValue = useMemo(() => {
    if (!userBirthdaySearch) {
      return 'Datum rodjenja';
    }

    const parsedDate = dayjs(userBirthdaySearch);

    return parsedDate.isValid()
      ? parsedDate.format('DD.MM.YYYY')
      : 'Datum rodjenja';
  }, [userBirthdaySearch]);

  const canApplyBirthdayFilter = useMemo(() => {
    return buildBirthdayIso(birthdayDraft) !== null;
  }, [birthdayDraft, buildBirthdayIso]);

  const handleBirthdayPickerOpenChange = useCallback(
    (open) => {
      setBirthdayPickerOpen(open);

      if (open) {
        setBirthdayDraft(formatBirthdayParts(userBirthdaySearch));
      }
    },
    [formatBirthdayParts, userBirthdaySearch]
  );

  const handleApplyBirthdayFilter = useCallback(() => {
    const nextBirthdayValue = buildBirthdayIso(birthdayDraft);

    if (nextBirthdayValue === null) {
      return;
    }

    setUserBirthdaySearch(nextBirthdayValue);
    setCurrentPage(1);
    setBirthdayPickerOpen(false);
  }, [birthdayDraft, buildBirthdayIso]);

  const handleClearBirthdayDraft = useCallback(() => {
    setBirthdayDraft({ day: '', month: '', year: '' });
    setUserBirthdaySearch('');
    setCurrentPage(1);
    setBirthdayPickerOpen(false);
  }, []);

  const handleClearBirthdayFilter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setBirthdayDraft({ day: '', month: '', year: '' });
    setUserBirthdaySearch('');
    setCurrentPage(1);
    setBirthdayPickerOpen(false);
  }, []);

  const birthdayPopoverContent = useMemo(
    () => (
      <div className="therapy-birthday-picker">
        <div className="therapy-birthday-picker__fields">
          <Input
            className="therapy-birthday-picker__field therapy-birthday-picker__field--day"
            value={birthdayDraft.day}
            placeholder="Dan"
            inputMode="numeric"
            maxLength={2}
            onChange={(event) =>
              updateBirthdayDraftField('day', event.target.value)
            }
          />
          <Input
            className="therapy-birthday-picker__field therapy-birthday-picker__field--month"
            value={birthdayDraft.month}
            placeholder="Mesec"
            inputMode="numeric"
            maxLength={2}
            onChange={(event) =>
              updateBirthdayDraftField('month', event.target.value)
            }
          />
          <Input
            className="therapy-birthday-picker__field therapy-birthday-picker__field--year"
            value={birthdayDraft.year}
            placeholder="Godina"
            inputMode="numeric"
            maxLength={4}
            onChange={(event) =>
              updateBirthdayDraftField('year', event.target.value)
            }
          />
        </div>
        <div className="therapy-birthday-picker__actions">
          <Button type="text" size="small" onClick={handleClearBirthdayDraft}>
            Ocisti
          </Button>
          <Button
            type="primary"
            size="small"
            disabled={!canApplyBirthdayFilter}
            onClick={handleApplyBirthdayFilter}
          >
            Primeni
          </Button>
        </div>
      </div>
    ),
    [
      birthdayDraft.day,
      birthdayDraft.month,
      birthdayDraft.year,
      canApplyBirthdayFilter,
      handleApplyBirthdayFilter,
      handleClearBirthdayDraft,
      updateBirthdayDraftField,
    ]
  );

  const birthdayFilter = useMemo(
    () => ({
      type: 'custom',
      className: 'therapyFilterDob',
      render: () => (
        <Popover
          trigger="click"
          placement="bottomLeft"
          open={birthdayPickerOpen}
          onOpenChange={handleBirthdayPickerOpenChange}
          content={birthdayPopoverContent}
          classNames={{ root: 'therapy-birthday-picker-popover' }}
        >
          <button type="button" className="therapy-birthday-picker-trigger">
            <span
              className={`therapy-birthday-picker-trigger__label${
                userBirthdaySearch ? ' is-filled' : ''
              }`}
            >
              {birthdayDisplayValue}
            </span>
            {userBirthdaySearch ? (
              <span
                role="button"
                tabIndex={0}
                className="therapy-birthday-picker-trigger__clear"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={handleClearBirthdayFilter}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    handleClearBirthdayFilter(event);
                  }
                }}
              >
                <CloseCircleFilled />
              </span>
            ) : null}
          </button>
        </Popover>
      ),
    }),
    [
      birthdayDisplayValue,
      birthdayPickerOpen,
      birthdayPopoverContent,
      handleClearBirthdayFilter,
      handleBirthdayPickerOpenChange,
      userBirthdaySearch,
    ]
  );

  const handleResetFilters = useCallback(() => {
    resetTableSearch();
    setUserBirthdaySearch('');
    setBirthdayPickerOpen(false);
    setBirthdayDraft({ day: '', month: '', year: '' });
    setFilter({});
    setSelectedUserIds([]);
    setCurrentPage(1);
  }, [resetTableSearch]);

  const hasActiveFilters = Boolean(
    tableSearch.isActive ||
      userBirthdaySearch ||
      filter.pickupMethod ||
      filter.isHealthBookletVerified
  );

  const columns = useMemo(
    () => [
      {
        key: 'firstName',
        label: 'Pacijent',
        render: (_, row) => {
          const fullName =
            `${row.lastName || ''} ${row.firstName || ''}`.trim();
          const label = fullName || row.email || row.phone || 'Korisnik';

          return (
            <span
              role="button"
              tabIndex={0}
              className={style.users__link}
              onClick={() => handleViewUserDetails(row._id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleViewUserDetails(row._id);
                }
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        key: 'dateOfBirth',
        label: 'Datum rođenja',
        render: (val) => (val ? dayjs(val).format('DD.MM.YYYY') : '-'),
      },
      {
        key: 'lbo',
        label: 'LBO',
        render: (val) => val || '-',
      },
      {
        key: 'healthBookletNumber',
        label: 'BZK',
        render: (val) => val || '-',
      },
      {
        key: 'isHealthBookletVerified',
        label: 'Overena ZK',
        render: (val) => (
          <StatusFlag tone={getBooleanTone(val)}>
            {getBooleanLabel(val)}
          </StatusFlag>
        ),
      },
      {
        key: 'phone',
        label: 'Broj telefona',
        render: (val) => val || '-',
      },
      {
        key: 'address',
        label: 'Adresa',
        render: (val) => formatAddress(val),
      },
      {
        key: 'pickupMethod',
        label: 'Preuzimanje',
        render: (val) => val || 'APO',
      },
      {
        key: 'internalNote',
        label: 'Interna napomena',
        render: (val) => val || '-',
      },
      {
        key: 'status',
        label: 'Status',
        render: (val) => (
          <StatusFlag tone={getAccountStatusTone(val)}>
            {statusLabel(val)}
          </StatusFlag>
        ),
      },
      {
        key: 'haveApp',
        label: 'App',
        render: (val) => (
          <StatusFlag tone={getBooleanTone(val)}>
            {getBooleanLabel(val)}
          </StatusFlag>
        ),
      },
      {
        key: 'hasDeviceToken',
        label: 'Slanje notifikacija',
        render: (val) => (
          <StatusFlag tone={getBooleanTone(val)}>
            {getBooleanLabel(val)}
          </StatusFlag>
        ),
      },
    ],
    []
  );
  const userFilterSections = useMemo(
    () => [
      {
        title: '',
        type: 'pickupMethod',
        buttonLabel: 'Preuzimanje',
        multiSelect: true,
        popupClassName: 'pickup-method-filter-dropdown',
        items: userPickupMethodOptions.map((option) => ({
          key: option.value,
          label: option.label,
        })),
      },
    ],
    []
  );
  const healthBookletVerifiedFilterSections = useMemo(
    () => [
      {
        title: '',
        type: 'isHealthBookletVerified',
        buttonLabel: 'Overena ZK',
        items: [
          { key: 'true', label: 'Da' },
          { key: 'false', label: 'Ne' },
        ],
      },
    ],
    []
  );
  // Abecedno po prezimenu (u okviru trenutne stranice). Globalno sortiranje
  // kroz sve stranice radi backend (v. prompt za BE).
  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) =>
        `${a?.lastName || ''}`.localeCompare(`${b?.lastName || ''}`, 'sr', {
          sensitivity: 'base',
        })
      ),
    [users]
  );
  const renderTable = (
    emptyState = {
      icon: '/assets/icons/no-users.svg',
      title: 'Trenutno nema korisnika',
    },
    showFilter = false
  ) => (
    <TableSection
      showFilter={showFilter}
      showCreateButton={false}
      createButtonDisplay="Dodaj pacijenta"
      tableMinWidth={1120}
      onCreateClick={() => setIsCreateUserModalOpen(true)}
      onSearch={() => {}}
      tableSearch={tableSearch}
      searchFieldOrder={['tableSearch', 'birthdayDateSearch', 'filter']}
      inlineHeaderActions={
        hasActiveFilters ? (
          <Button
            type="default"
            className="btn-reset-filters"
            onClick={handleResetFilters}
          >
            <span className="btn-reset-filters__content">
              Ponisti filtere
              <CloseOutlined />
            </span>
          </Button>
        ) : null
      }
      rightHeaderActions={
        <div className={style.users__header__rightActions}>
          <Button
            className={style.users__header__addButton}
            type="primary"
            onClick={() => setIsCreateUserModalOpen(true)}
          >
            Dodaj pacijenta
          </Button>
        </div>
      }
      birthdayDateSearch={birthdayFilter}
      filter={{
        selected: filter,
        sections: [userFilterSections, healthBookletVerifiedFilterSections],
        layoutClassName: 'therapyFilterPickup',
      }}
      onFilterChange={handleFilterChange}
      columns={columns}
      actions={[
        {
          key: 'view-therapies',
          label: 'Pogledaj terapije',
          icon: (
            <img
              src="/assets/icons/view-icon.svg"
              alt="Pogledaj terapije"
              width={16}
              height={16}
              className="dropdown-icon"
            />
          ),
          onClick: (rowUser) => handleOpenTherapies(rowUser),
          disabled: (rowUser) => therapiesLoadingUserId === rowUser?._id,
        },
        {
          key: 'add-therapy',
          label: 'Dodaj terapiju',
          icon: (
            <img
              src="/assets/icons/add.svg"
              alt="Dodaj terapiju"
              width={16}
              height={16}
              className="dropdown-icon"
            />
          ),
          onClick: (rowUser) => handleOpenAddTherapy(rowUser),
          visible: () => isAdmin(user),
        },
        {
          key: 'notify',
          label: 'Posalji notifikaciju',
          icon: (
            <img
              src="/assets/icons/mail.svg"
              alt="Notify"
              width={16}
              height={16}
              className="dropdown-icon"
            />
          ),
          onClick: (rowUser) => handleOpenNotification(rowUser),
          visible: () => isAdmin(user),
        },
        {
          key: 'update',
          label: 'Azuriraj korisnika',
          icon: <img src="/assets/icons/edit-icon.svg" alt="Izmeni" />,
          onClick: (rowUser) => handleUpdateUser(rowUser._id),
        },
        {
          key: 'active',
          label: 'Aktiviraj nalog',
          icon: <img src="/assets/icons/active.svg" alt="Active" />,
          onClick: (rowUser) => handleActiveUser(rowUser._id),
          visible: (rowUser) => rowUser.status !== 'active',
        },
        {
          key: 'block',
          label: 'Deaktiviraj nalog',
          icon: <img src="/assets/icons/block.svg" alt="Archive" />,
          onClick: (rowUser) => handleOpenBlockModal(rowUser),
          visible: (rowUser) => rowUser.status === 'active',
        },
        ...(isAdmin(user)
          ? [
              {
                key: 'delete',
                label: 'Trajno deaktiviraj nalog',
                icon: <img src="/assets/icons/delete.svg" alt="Obriši" />,
                onClick: (rowUser) => {
                  setDeletePassword('');
                  setDeleteError('');
                  setDone(false);
                  setUserForDelete(rowUser);
                  setIsDeletePasswordVisible(false);
                  setDeleteUser(rowUser._id);
                  setIsDeleteModalOpen(true);
                },
                visible: (rowUser) => rowUser.status !== 'deleted',
                danger: true,
              },
              // {
              //   key: 'erase',
              //   label: 'Trajno obriši',
              //   icon: <img src="/assets/icons/delete.svg" alt="Erase" />,
              //   onClick: handleOpenEraseModal,
              //   // Samo pacijenti; admin/pharmacy nalog bek ionako odbija
              //   // (USER_NOT_PATIENT). Dostupno i za vec deaktivirane pacijente.
              //   visible: (rowUser) => (rowUser.role || 'user') === 'user',
              //   danger: true,
              // },
            ]
          : []),
      ]}
      data={sortedUsers}
      selectable={false}
      selectedRowKeys={selectedUserIds}
      onSelectionChange={setSelectedUserIds}
      bulkActionContent={
        <>
          <span>{`Selektovano: ${selectedUserIds.length}`}</span>
          <Select
            value={bulkStatus}
            onChange={setBulkStatus}
            style={{ minWidth: 180 }}
            options={[
              { value: 'active', label: 'Aktivan' },
              { value: 'blocked', label: 'Blokiran' },
            ]}
          />
          <Button
            type="primary"
            loading={bulkLoading}
            onClick={handleBulkStatusUpdate}
          >
            Primeni status
          </Button>
        </>
      }
      emptyState={emptyState}
      showPagination
      currentPage={currentPage}
      totalPages={totalPages}
      totalResults={totalResults}
      paginationLoading={listLoading}
      onPageChange={setCurrentPage}
      searchPlaceholder="Pretraži"
    />
  );

  return (
    <>
      <ContentLayout>
        {renderTable(
          {
            icon: '/assets/icons/no-users.svg',
            title: 'Trenutno nema korisnika',
            description: 'Napravite pacijenta',
            actionButton: {
              label: 'Dodaj pacijenta',
              onClick: () => setIsCreateUserModalOpen(true),
              type: 'primary',
              style: { margin: '16px auto 0px auto' },
            },
          },
          true
        )}
      </ContentLayout>
      <CreateUserModal
        isOpen={isCreateUserModalOpen}
        onClose={handleCreateUserModalClose}
        onCreate={loadUsers}
      />
      <ImportUsersModal
        isOpen={isImportUsersModalOpen}
        onClose={() => setIsImportUsersModalOpen(false)}
        onImported={loadUsers}
      />
      <UpdateUserModal
        isOpen={isUpdateUserModalOpen}
        user={userForUpdate}
        loading={updateLoading}
        onClose={handleUpdateModalClose}
        onPasswordSubmit={handleUpdateUserPasswordSubmit}
        onSubmit={handleUpdateUserSubmit}
        onRefreshUser={handleRefreshUserForUpdate}
      />
      <ViewUserDetailsModal
        isOpen={isViewUserDetailsModalOpen}
        user={userForView}
        loading={viewUserDetailsLoading}
        onClose={handleViewUserDetailsModalClose}
        onPasswordSubmit={handleViewUserPasswordSubmit}
        onSubmit={handleViewUserDetailsSubmit}
        onRefreshUser={handleRefreshUserForView}
      />
      <DeleteModal
        className="delete_user_modal"
        visible={isBlockModalOpen}
        onDelete={handleConfirmBlockUser}
        onCancel={handleCloseBlockModal}
        loading={isDeleteLoading}
        done={done}
        title="Deaktivacija korisnika"
        message={`Da li ste sigurni da zelite da deaktivirate korisnika ${getUserDisplayName(
          userForBlock
        )}?`}
        buttonText="Deaktiviraj"
      />
      <DeleteModal
        className="delete_user_modal"
        visible={isDeleteModalOpen}
        onDelete={handleDeleteUser}
        onCancel={handleClosePermanentDeleteModal}
        loading={isDeleteLoading}
        done={done}
        title="Trajna deaktivacija korisnika"
        message={`Unesite svoj PIN da biste trajno deaktivirali korisnika ${getUserDisplayName(
          userForDelete
        )}.`}
        buttonText="Trajno deaktiviraj"
        confirmDisabled={!deletePassword.trim()}
      >
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Input
            type={isDeletePasswordVisible ? 'text' : 'password'}
            placeholder="Vas PIN"
            inputMode="numeric"
            value={deletePassword}
            onChange={(e) => {
              setDeletePassword(e.target.value);
              if (deleteError) setDeleteError('');
            }}
          />
          <Button
            type="default"
            onClick={() =>
              setIsDeletePasswordVisible((currentValue) => !currentValue)
            }
            aria-label={isDeletePasswordVisible ? 'Sakrij PIN' : 'Prikazi PIN'}
          >
            {isDeletePasswordVisible ? (
              <EyeInvisibleOutlined />
            ) : (
              <EyeOutlined />
            )}
          </Button>
        </div>
        {!!deleteError && (
          <Alert
            title={deleteError}
            type="error"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </DeleteModal>
      <PermanentDeleteUserModal
        isOpen={isEraseModalOpen}
        user={userForErase}
        userDisplayName={getUserDisplayName(userForErase)}
        onClose={handleCloseEraseModal}
        onDeleted={handleUserErased}
        onNotFound={() => loadUsers()}
      />
      <SendNotificationModal
        isOpen={isNotificationModalOpen}
        onClose={handleCloseNotification}
        user={userForNotification}
      />
      <TherapyOccurrencesModal
        isOpen={isTherapiesModalOpen}
        onClose={handleCloseTherapies}
        therapy={therapyForOccurrences}
        onRefetchTherapies={loadUsers}
        onReloadTherapyContext={handleReloadTherapyContext}
      />
      <AddTherapyModal
        isOpen={isTherapyCreateOpen}
        onClose={handleCloseAddTherapy}
        user={userForTherapyCreate}
        onCreate={loadUsers}
      />
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </>
  );
}
